import { useCallback, useEffect, useRef, useState } from 'react'

import { type MessageInitShape, create } from '@bufbuild/protobuf'
import { ConnectError } from '@connectrpc/connect'
import {
  AggregateEditIntentSchema,
  DemandForecastAggregateEditPreviewErrorReason,
  DemandForecastAggregateEditPreviewErrorReasonSchema,
  PreviewAggregateEditRequestSchema,
  type PreviewAggregateEditResponse,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { parseAggregateInteger } from '~/pages/DemandForecastDaily/models/aggregateEdit'
import {
  adoptAggregateResult,
  aggregateFingerprint,
  ownSaveAcknowledgementsReflected,
  projectOwnSaveAcknowledgements,
} from '~/pages/DemandForecastDaily/models/aggregateSession'
import { protoSlotsToKoma } from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  DraftSaveAcknowledgement,
  KomaSlots,
  RetailerEditRow,
} from '~/pages/DemandForecastDaily/models/types'

import { usePreviewAggregateEdit } from './usePreviewAggregateEdit'

type Request = MessageInitShape<typeof PreviewAggregateEditRequestSchema>
type Intent = MessageInitShape<typeof AggregateEditIntentSchema>
type Context = {
  identity: string
  rows: Readonly<Record<string, RetailerEditRow>>
  serverRows: Readonly<Record<string, RetailerEditRow>>
  gc: number
  request: Request
  total: KomaSlots
}
type Session = {
  context: Context
  intents: Intent[]
  revision: number
  display: KomaSlots
  serverConflict?: boolean
  ready?: {
    revision: number
    gc: number
    response: PreviewAggregateEditResponse
  }
}

type Captured = { rows: Record<string, RetailerEditRow>; reverted: number[] }
type AggregateSessionResult = {
  acknowledgeSave: (ack: DraftSaveAcknowledgement) => void
  edit: (
    intent: Intent,
    changes?: Array<{ koma: number; value: number | undefined }>,
  ) => void
  capture: () => Promise<Captured | undefined>
  discard: (keys?: readonly string[]) => void
  undo: () => void
  hasPending: () => boolean
  pending: boolean
  busy: boolean
  requesting: boolean
  display: KomaSlots | undefined
  parseInput: typeof parseAggregateInteger
  getRevision: () => number
  dependsOn: (keys: readonly string[]) => boolean
  conflictKeys: string[]
  revision: number
}

function displayFromIntents(
  total: KomaSlots,
  gc: number,
  intents: readonly Intent[],
  displayChanges: ReadonlyArray<{
    koma: number
    value: number | undefined
  }> = [],
): KomaSlots {
  const display = [...total]
  for (const intent of intents) {
    if (intent.kind?.case !== 'setSlots') continue
    for (const change of intent.kind.value.changes ?? []) {
      if (change.koma !== undefined && change.koma > gc)
        display[change.koma - 1] =
          change.valueKwh === undefined ? undefined : Number(change.valueKwh)
    }
  }
  for (const change of displayChanges)
    if (change.koma > gc) display[change.koma - 1] = change.value
  return display
}

/** Immutable basis and revisioned intent buffer. No leaf writes occur here. */
export function useAggregateSession(
  getContext: () => Context,
  onError: (error?: ConnectError) => void,
): AggregateSessionResult {
  const preview = usePreviewAggregateEdit()
  const current = useRef(getContext)
  current.current = getContext
  const errorCallback = useRef(onError)
  errorCallback.current = onError
  const session = useRef<Session | undefined>(undefined)
  const acknowledgements = useRef<DraftSaveAcknowledgement[]>([])
  const revisionClock = useRef(0)
  const [version, setVersion] = useState(0)
  const [busy, setBusy] = useState(false)
  const [waiting, setWaiting] = useState<{
    session: Session
    revision: number
    gc: number
  }>()
  const [, renderFailure] = useState(0)
  const alive = useRef(0)
  const waiters = useRef(new Set<() => void>())
  const notifyChange = useCallback(() => {
    for (const wake of waiters.current) wake()
  }, [])
  useEffect(() => {
    alive.current += 1
    return () => {
      alive.current += 1
      notifyChange()
    }
  }, [notifyChange])
  const refresh = useCallback(() => {
    notifyChange()
    setVersion((v) => v + 1)
  }, [notifyChange])
  const requestRef = useRef(preview.mutateAsync)
  requestRef.current = preview.mutateAsync
  const requestRun = useRef<
    | {
        session: Session
        revision: number
        gc: number
        promise: Promise<PreviewAggregateEditResponse>
      }
    | undefined
  >(undefined)
  const captureRun = useRef<
    | Promise<
        | { rows: Record<string, RetailerEditRow>; reverted: number[] }
        | undefined
      >
    | undefined
  >(undefined)

  const scopedAcknowledgements = useCallback(
    () =>
      acknowledgements.current.filter((ack) => {
        const request = current.current().request
        return (
          ack.saveContext.caseId === request.caseId &&
          ack.saveContext.areaCode === request.areaCode &&
          ack.saveContext.section === request.section &&
          ack.saveContext.targetDate?.year === request.targetDate?.year &&
          ack.saveContext.targetDate?.month === request.targetDate?.month &&
          ack.saveContext.targetDate?.day === request.targetDate?.day
        )
      }),
    [],
  )
  const projectedRows = useCallback(
    (
      rows: Readonly<Record<string, RetailerEditRow>>,
      view: 'actor' | 'server' = 'actor',
    ) => projectOwnSaveAcknowledgements(rows, scopedAcknowledgements(), view),
    [scopedAcknowledgements],
  )
  const acknowledgeSave = useCallback(
    (ack: DraftSaveAcknowledgement): void => {
      acknowledgements.current.push(structuredClone(ack))
      refresh()
    },
    [refresh],
  )

  const matches = useCallback(
    (s: Session): boolean => {
      const c = current.current()
      return (
        c.identity === s.context.identity &&
        aggregateFingerprint(projectedRows(c.rows)) ===
          aggregateFingerprint(projectedRows(s.context.rows)) &&
        aggregateFingerprint(projectedRows(c.serverRows, 'server')) ===
          aggregateFingerprint(projectedRows(s.context.serverRows, 'server'))
      )
    },
    [projectedRows],
  )

  const fetchLatest = useCallback(
    async (s: Session): Promise<PreviewAggregateEditResponse | undefined> => {
      const generation = alive.current
      while (session.current === s && generation === alive.current) {
        if (!matches(s)) {
          errorCallback.current()
          return undefined
        }
        const revision = s.revision
        const gc = current.current().gc
        if (s.ready?.revision === revision && s.ready.gc === gc)
          return s.ready.response
        let run = requestRun.current
        if (run?.session !== s || run.revision !== revision || run.gc !== gc) {
          run = {
            session: s,
            revision,
            gc,
            promise: requestRef.current({
              ...s.context.request,
              intents: s.intents.map((i) =>
                create(AggregateEditIntentSchema, i),
              ),
            }),
          }
          requestRun.current = run
          setWaiting(run)
        }
        let response: PreviewAggregateEditResponse | undefined
        let wake!: () => void
        const changed = new Promise<undefined>((resolve) => {
          wake = () => resolve(undefined)
          waiters.current.add(wake)
        })
        try {
          try {
            response = await Promise.race([run.promise, changed])
          } finally {
            waiters.current.delete(wake)
            setWaiting((waiting) => (waiting === run ? undefined : waiting))
          }
        } catch (error) {
          if (requestRun.current === run) requestRun.current = undefined
          if (
            session.current === s &&
            generation === alive.current &&
            (revision !== s.revision || gc !== current.current().gc)
          )
            continue
          if (
            session.current === s &&
            revision === s.revision &&
            generation === alive.current
          ) {
            const connectError = ConnectError.from(error)
            s.serverConflict =
              readAppErrorInfo(
                connectError,
                DemandForecastAggregateEditPreviewErrorReasonSchema,
              )?.reason ===
              DemandForecastAggregateEditPreviewErrorReason.CONTEXT_CHANGED
            errorCallback.current(connectError)
            renderFailure((v) => v + 1)
          }
          return undefined
        }
        if (
          session.current !== s ||
          generation !== alive.current ||
          !matches(s)
        )
          return undefined
        if (response === undefined) continue
        if (revision !== s.revision || gc !== current.current().gc) continue
        if (
          adoptAggregateResult(current.current().rows, response, gc) ===
          undefined
        ) {
          requestRun.current = undefined
          errorCallback.current()
          return undefined
        }
        s.serverConflict = false
        s.ready = { revision, gc, response }
        const total = protoSlotsToKoma(response.effectiveTotalSlots)
        s.display = total.map((v, i) =>
          i < gc ? current.current().total[i] : v,
        )
        refresh()
        return response
      }
      return undefined
    },
    [matches, refresh],
  )

  const edit = useCallback(
    (
      intent: Intent,
      displayChanges?: Array<{ koma: number; value: number | undefined }>,
    ): void => {
      const context = current.current()
      let s = session.current
      if (s === undefined) {
        s = {
          context: structuredClone(context),
          intents: [],
          revision: 0,
          display: [...context.total],
        }
        session.current = s
      }
      // A changed partition/basis requires explicit discard/reload; never rebase.
      if (!matches(s)) {
        errorCallback.current()
        return
      }
      s.intents = [...s.intents, structuredClone(intent)]
      s.revision = ++revisionClock.current
      s.ready = undefined
      // Only raw entered cells are displayed optimistically. Derived slots await
      // this revision's server result rather than displaying an old preview.
      s.display = displayFromIntents(
        context.total,
        context.gc,
        s.intents,
        displayChanges,
      )
      refresh()
    },
    [matches, refresh],
  )

  useEffect(() => {
    if (session.current !== undefined) return
    if (captureRun.current !== undefined) return
    const { rows, serverRows } = current.current()
    if (
      ownSaveAcknowledgementsReflected(
        rows,
        scopedAcknowledgements(),
        'actor',
      ) &&
      ownSaveAcknowledgementsReflected(
        serverRows,
        scopedAcknowledgements(),
        'server',
      )
    )
      acknowledgements.current = []
  })

  const liveGc = getContext().gc

  // Coalesce edits within one render. Capture uses the same revision request
  // immediately, without depending on this effect having run.
  useEffect(() => {
    notifyChange()
    const s = session.current
    if (s === undefined) return
    void fetchLatest(s)
  }, [version, liveGc, fetchLatest, notifyChange])

  const capture = useCallback((): Promise<
    { rows: Record<string, RetailerEditRow>; reverted: number[] } | undefined
  > => {
    if (captureRun.current !== undefined) return captureRun.current
    const s = session.current
    if (s === undefined)
      return Promise.resolve({
        rows: structuredClone(projectedRows(current.current().rows)),
        reverted: [],
      })
    setBusy(true)
    const run = (async () => {
      const response = await fetchLatest(s)
      if (
        response === undefined ||
        session.current !== s ||
        !matches(s) ||
        s.ready?.revision !== s.revision ||
        s.ready.gc !== current.current().gc
      )
        return undefined
      const rows = adoptAggregateResult(
        projectedRows(current.current().rows),
        response,
        current.current().gc,
      )
      if (rows === undefined) return undefined
      session.current = undefined
      refresh()
      return { rows, reverted: response.revertedKomas }
    })().finally(() => {
      captureRun.current = undefined
      setBusy(false)
    })
    captureRun.current = run
    return run
  }, [fetchLatest, matches, refresh, projectedRows])

  const dependsOn = useCallback((keys: readonly string[]): boolean => {
    const s = session.current
    if (s === undefined) return false
    const scope = s.context.request.scope?.kind
    return Object.entries(s.context.rows).some(
      ([key, row]) =>
        keys.includes(key) &&
        (scope?.case === 'bgTotal' ||
          (scope?.case === 'retailerTotal' &&
            scope.value.retailerBusinessCd === row.retailerBusinessCd)),
    )
  }, [])
  const discard = useCallback(
    (keys?: readonly string[]): void => {
      if (keys === undefined || dependsOn(keys)) {
        session.current = undefined
        refresh()
      }
    },
    [refresh, dependsOn],
  )
  const undo = useCallback((): void => {
    const s = session.current
    if (s === undefined) return
    if (s.intents.length <= 1) {
      discard()
      return
    }
    s.intents = s.intents.slice(0, -1)
    s.revision = ++revisionClock.current
    s.ready = undefined
    s.display = displayFromIntents(
      s.context.total,
      current.current().gc,
      s.intents,
    )
    refresh()
  }, [discard, refresh])
  const s = session.current
  const conflictKeys =
    s !== undefined && (s.serverConflict === true || !matches(s))
      ? Object.keys(s.context.rows).filter((key) => dependsOn([key]))
      : []
  const display = (s !== undefined && matches(s) ? s.display : undefined)?.map(
    (v, i) => (i < current.current().gc ? current.current().total[i] : v),
  )
  return {
    acknowledgeSave,
    edit,
    capture,
    discard,
    undo,
    hasPending: () => session.current !== undefined,
    pending: s !== undefined,
    busy,
    requesting:
      s !== undefined &&
      waiting?.session === s &&
      waiting.revision === s.revision &&
      waiting.gc === liveGc,
    display,
    parseInput: parseAggregateInteger,
    getRevision: () => session.current?.revision ?? 0,
    dependsOn,
    conflictKeys,
    revision: s?.revision ?? 0,
  }
}
